import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../../lib/network/api";
import { ApiError, ApiResponse } from "../../../../lib/network/axios";
import { toast } from "sonner";
import { AddPaymentMethodPayload } from "../types/settings";

// Add Payment Method
export const addPaymentMethod = async (
  id: string,
  payload: AddPaymentMethodPayload
): Promise<ApiResponse> => {
  const res = await api.post<ApiResponse>(
    `/users/${id}/payment-methods`,
    payload
  );
  return res;
};

export const useAddPaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse,
    ApiError,
    { id: string; payload: AddPaymentMethodPayload }
  >({
    mutationFn: ({ id, payload }) => addPaymentMethod(id, payload),
    onSuccess: (response) => {
      toast.success("Payment Method Added", {
        description:
          response.message ||
          "Your payment method has been added successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["userDetails"] });
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error.response?.data?.fields?.[0]?.message ||
        error.response?.data?.message ||
        "Failed to add payment method. Please try again.";

      toast.error("Failed to Add Payment Method", {
        description: errorMessage,
      });
    },
  });
};

// Set Default Payment Method
export const setDefaultPaymentMethod = async (
  id: string,
  paymentMethodId: string
): Promise<ApiResponse> => {
  const res = await api.patch<ApiResponse>(
    `/users/${id}/payment-methods/${paymentMethodId}/default`
  );
  return res;
};

export const useSetDefaultPaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse,
    ApiError,
    { id: string; paymentMethodId: string }
  >({
    mutationFn: ({ id, paymentMethodId }) =>
      setDefaultPaymentMethod(id, paymentMethodId),
    onSuccess: (response) => {
      toast.success("Default Payment Updated", {
        description:
          response.message || "Your default payment method has been updated.",
      });
      queryClient.invalidateQueries({ queryKey: ["userDetails"] });
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error.response?.data?.fields?.[0]?.message ||
        error.response?.data?.message ||
        "Failed to update default payment method.";

      toast.error("Update Failed", {
        description: errorMessage,
      });
    },
  });
};

// Delete Payment Method
export const deletePaymentMethod = async (
  id: string,
  paymentMethodId: string
): Promise<ApiResponse> => {
  const res = await api.delete<ApiResponse>(
    `/users/${id}/payment-methods/${paymentMethodId}`
  );
  return res;
};

export const useDeletePaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse,
    ApiError,
    { id: string; paymentMethodId: string }
  >({
    mutationFn: ({ id, paymentMethodId }) =>
      deletePaymentMethod(id, paymentMethodId),
    onSuccess: (response) => {
      toast.success("Payment Method Removed", {
        description:
          response.message || "Your payment method has been removed.",
      });
      queryClient.invalidateQueries({ queryKey: ["userDetails"] });
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error.response?.data?.fields?.[0]?.message ||
        error.response?.data?.message ||
        "Failed to remove payment method.";

      toast.error("Removal Failed", {
        description: errorMessage,
      });
    },
  });
};
